import { ButtonType, ButtonWidget, FlyoutListMenuWidget, MenuButtonWidget } from '@tableau/widgets';
import React from 'react';
import * as TypeStyle from 'typestyle';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { Icons } from '~src/commonInfra/components/Icons';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';

import { IPackage } from '../models/IPackage';
import {
  PACKAGES_EMPTY_TEST_ID,
  PACKAGES_ROW_ACTIONS_TEST_ID,
  PACKAGES_SECTION_TEST_ID,
  PACKAGES_UPLOAD_BUTTON_TEST_ID,
} from '../PackagesTestIds';
import { DeletePackageService } from '../serverApi/requests/DeletePackage';
import { ListPackagesService } from '../serverApi/requests/ListPackages';
import { SetPackageDefaultActivationService } from '../serverApi/requests/SetPackageDefaultActivation';
import { SetPackageEnabledService } from '../serverApi/requests/SetPackageEnabled';
import { UploadPackageDialog } from './UploadPackageDialog';

// Site-admin section for managing uploaded .tpkg packages. Each row exposes
// two independent toggles: Enabled (kill-switch) and Default activation
// (on-by-default policy). See analysis/packages-sharing-and-state-model.md.
//
// Phase 1: standalone list with hand-rolled table, local fetch, and an inline
// action menu — matching the convention of other admin settings tables. When
// Packages become permissioned and appear in cross-entity lists, this will
// move to the Explorer / IEntityAction pipeline. The TODOs in
// SortOptionDefinitions.ts and SiteUserSpaceProvider.tsx flag the related
// branches to revisit at that point.
export function PackagesSiteSection(): React.ReactElement | null {
  const [packages, setPackages] = React.useState<IPackage[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const loadPackages = React.useCallback(() => {
    setPackages(null);
    setLoadError(null);
    return ListPackagesService.listPackages()
      .then(response => {
        setPackages(response.packages?.package ?? []);
      })
      .catch((err: { data?: { error?: { detail?: string } } }) => {
        setLoadError(err?.data?.error?.detail ?? 'Failed to load packages');
      });
  }, []);

  React.useEffect(() => { loadPackages(); }, [loadPackages]);

  const openUploadDialog = React.useCallback((targetPackage?: IPackage) => {
    const modal = ReactModalService.openCustom<boolean>(closeAndDismiss => (
      <UploadPackageDialog
        close={closeAndDismiss.close}
        dismiss={closeAndDismiss.dismiss}
        targetPackage={targetPackage} />
    ));
    modal.result.then(success => {
      if (success) {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: targetPackage ? Messages.packages_update_success() : Messages.packages_upload_success(),
        });
        loadPackages();
      }
    }, () => undefined);
  }, [loadPackages]);

  const handleToggleEnabled = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const next = !pkg.enabled;
    SetPackageEnabledService.setPackageEnabled(pkg.luid, next)
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.packages_setEnabled_success(),
        });
        loadPackages();
      })
      .catch(() => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          message: Messages.packages_setEnabled_error(),
        });
      });
  }, [loadPackages]);

  const handleToggleDefaultActivation = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const next = !pkg.defaultActivation;
    SetPackageDefaultActivationService.setPackageDefaultActivation(pkg.luid, next)
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Success,
          message: Messages.packages_setDefaultActivation_success(),
        });
        loadPackages();
      })
      .catch(() => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          message: Messages.packages_setDefaultActivation_error(),
        });
      });
  }, [loadPackages]);

  const handleUpdate = React.useCallback((pkg: IPackage) => {
    openUploadDialog(pkg);
  }, [openUploadDialog]);

  // TODO: open sharing dialog once non-owner grants ship.
  const handleShare = React.useCallback((_pkg: IPackage) => undefined, []);

  const handleDelete = React.useCallback((pkg: IPackage) => {
    if (!pkg.luid) {
      return;
    }
    const luid = pkg.luid;
    openReactConfirmationDialog<string, void>({
      title: Messages.packages_delete_dialog_title(),
      message: Messages.packages_delete_dialog_message(),
      actionName: Messages.packages_actions_delete(),
      isActionDelete: true,
      paramsForAction: luid,
      action: (id: string) => DeletePackageService.deletePackage(id),
      dialogWidth: TabDialogWidth.width500px,
    }).result.then(() => {
      ToasterService.instance.pop({
        type: ToastType.Success,
        message: Messages.packages_delete_success(),
      });
      loadPackages();
    }, () => undefined);
  }, [loadPackages]);

  return (
    <SettingsSection
      title={Messages.packages_header()}
      testId={PACKAGES_SECTION_TEST_ID}
      description={Messages.packages_description()}>
      <div style={{ marginBottom: 12 }}>
        <ButtonWidget
          buttonType={ButtonType.Primary}
          children={Messages.packages_uploadButton()}
          handleClick={() => openUploadDialog()}
          testId={PACKAGES_UPLOAD_BUTTON_TEST_ID} />
      </div>
      <PackagesTable
        packages={packages}
        loadError={loadError}
        onToggleEnabled={handleToggleEnabled}
        onToggleDefaultActivation={handleToggleDefaultActivation}
        onShare={handleShare}
        onUpdate={handleUpdate}
        onDelete={handleDelete} />
    </SettingsSection>
  );
}

interface PackagesTableProps {
  packages: IPackage[] | null;
  loadError: string | null;
  onToggleEnabled: (pkg: IPackage) => void;
  onToggleDefaultActivation: (pkg: IPackage) => void;
  onShare: (pkg: IPackage) => void;
  onUpdate: (pkg: IPackage) => void;
  onDelete: (pkg: IPackage) => void;
}

function PackagesTable(props: PackagesTableProps) {
  const { packages, loadError, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete } = props;

  if (loadError) {
    return <div className="tb-warning-text">{loadError}</div>;
  }

  if (packages === null) {
    return <div>{'Loading...'}</div>;
  }

  if (packages.length === 0) {
    return (
      <div data-tb-test-id={PACKAGES_EMPTY_TEST_ID}>
        {Messages.packages_empty()}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={cellStyle}>{'Name'}</th>
          <th style={cellStyle}>{'Actions'}</th>
          <th style={cellStyle}>{'Package ID'}</th>
          <th style={cellStyle}>{'Version'}</th>
          <th style={cellStyle}>{'Author'}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_enabled()}</th>
          <th style={cellStyle}>{Messages.packages_columnHeader_defaultActivation()}</th>
          <th style={cellStyle}>{'Extensions'}</th>
          <th style={cellStyle}>{'Uploaded'}</th>
        </tr>
      </thead>
      <tbody>
        {packages.map(pkg => (
          <tr key={pkg.luid}>
            <td style={cellStyle}>
              <PackageNameLink pkg={pkg} />
            </td>
            <td style={cellStyle}>
              <PackageRowActions
                pkg={pkg}
                onToggleEnabled={onToggleEnabled}
                onToggleDefaultActivation={onToggleDefaultActivation}
                onShare={onShare}
                onUpdate={onUpdate}
                onDelete={onDelete} />
            </td>
            <td style={cellStyle}>{pkg.manifestId}</td>
            <td style={cellStyle}>{pkg.version}</td>
            <td style={cellStyle}>{pkg.author}</td>
            <td style={cellStyle}>{enabledLabel(pkg)}</td>
            <td style={cellStyle}>{defaultActivationLabel(pkg)}</td>
            <td style={cellStyle}>{pkg.extensions?.extension?.length ?? 0}</td>
            <td style={cellStyle}>{pkg.createdAt}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function enabledLabel(pkg: IPackage): string {
  return pkg.enabled
    ? Messages.packages_state_on()
    : Messages.packages_state_off();
}

function defaultActivationLabel(pkg: IPackage): string {
  return pkg.defaultActivation
    ? Messages.packages_state_on()
    : Messages.packages_state_off();
}

function PackageRowActions(props: {
  pkg: IPackage;
  onToggleEnabled: (pkg: IPackage) => void;
  onToggleDefaultActivation: (pkg: IPackage) => void;
  onShare: (pkg: IPackage) => void;
  onUpdate: (pkg: IPackage) => void;
  onDelete: (pkg: IPackage) => void;
}): JSX.Element {
  const { pkg, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete } = props;
  const buttonRef = React.useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen(o => !o), []);

  const handleSelect = React.useCallback((value: string) => {
    setIsOpen(false);
    if (value === 'toggleEnabled') {
      onToggleEnabled(pkg);
    } else if (value === 'toggleDefaultActivation') {
      onToggleDefaultActivation(pkg);
    } else if (value === 'share') {
      onShare(pkg);
    } else if (value === 'update') {
      onUpdate(pkg);
    } else if (value === 'delete') {
      onDelete(pkg);
    }
  }, [pkg, onToggleEnabled, onToggleDefaultActivation, onShare, onUpdate, onDelete]);

  const isEnabled = !!pkg.enabled;

  const menuItemInfos = [
    {
      value: 'toggleEnabled',
      displayValue: isEnabled
        ? Messages.packages_actions_setEnabledOff()
        : Messages.packages_actions_setEnabledOn(),
    },
    {
      value: 'toggleDefaultActivation',
      displayValue: Messages.packages_actions_toggleDefaultActivation(),
    },
    { value: 'share', displayValue: Messages.packages_actions_share() },
    { value: 'update', displayValue: Messages.packages_actions_update() },
    { value: 'delete', displayValue: Messages.packages_actions_delete() },
  ];

  const flyoutId = `packages-row-actions-flyout-${pkg.luid ?? ''}`;
  const testId = `${PACKAGES_ROW_ACTIONS_TEST_ID}-${pkg.luid ?? ''}`;

  return (
    <span className={inlineBlockClass}>
      <MenuButtonWidget
        aria-controls={flyoutId}
        aria-label={Messages.moreActions()}
        className={moreActionsButtonStyleName}
        onClick={toggle}
        ref={buttonRef}
        testId={testId}>
        <Icons.actionIcon />
      </MenuButtonWidget>
      {isOpen && (
        <FlyoutListMenuWidget
          allowAutoPosition
          anchorEl={buttonRef.current}
          id={flyoutId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ minWidth: 144 }}
          onSelect={handleSelect}
          requestCloseAll={close}
          testId={`${testId}-flyout`}
          toggleEl={buttonRef.current} />
      )}
    </span>
  );
}

function PackageNameLink(props: { pkg: IPackage }): JSX.Element {
  const { pkg } = props;
  const display = pkg.name ?? pkg.manifestId ?? '';
  if (!pkg.luid) {
    return <span>{display}</span>;
  }
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  if (!siteUrls) {
    return <span>{display}</span>;
  }
  return <a href={siteUrls.packageDetailsUrl(pkg.luid)}>{display}</a>;
}

const cellStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderBottom: '1px solid #ddd',
  textAlign: 'left',
};

const inlineBlockClass = TypeStyle.style({
  display: 'inline-block',
});

const moreActionsButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
    '&&::after': { display: 'none' },
  },
});
